// Professional Auto Ads Manager for React SPA
class AutoAdsManager {
  constructor() {
    this.isInitialized = false;
    this.currentPath = '';
    this.init();
    
    // Global protection against duplicate Auto Ads configuration
    this.setupGlobalProtection();
  }

  init() {
    if (typeof window === 'undefined') return;
    
    // Wait for AdSense script to load
    this.waitForAdSense();
  }

  waitForAdSense() {
    const checkAdSense = () => {
      if (window.adsbygoogle && window.adsbygoogle.push) {
        this.isInitialized = true;
        return true;
      }
      return false;
    };

    if (!checkAdSense()) {
      // Retry every 100ms for up to 5 seconds
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        attempts++;
        if (checkAdSense() || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 100);
    }
  }

  // Request ads for new page/route - optimized for mobile performance
  requestAds(newPath) {
    if (!this.isInitialized || !window.adsbygoogle) {
      return false;
    }

    // Only request if path actually changed
    if (this.currentPath === newPath) {
      return false;
    }

    // Rate limit to avoid jamming thread during fast navigation (max once per 2 seconds)
    const now = Date.now();
    if (this.lastRequestTime && now - this.lastRequestTime < 2000) {
      return false;
    }
    this.lastRequestTime = now;
    this.currentPath = newPath;

    // Delay ad push slightly on mobile to ensure vignette close transitions complete
    const adDelay = window.innerWidth <= 768 ? 2000 : 1000;
    const runWhenIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, adDelay));
    
    runWhenIdle(() => {
      try {
        // Safety check: if our document is currently locked by a vignette, don't double-push
        if (document.documentElement.style.overflow === 'hidden' && !document.documentElement.classList.contains('mobile-nav-open')) {
          console.log('⏳ AdSense refresh deferred: Ad interaction in progress');
          return;
        }

        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // Silently catch push errors
      }
    }, { timeout: 3000 });

    return true;
  }

  // Enable Auto Ads (called once on app start)
  enableAutoAds() {
    if (!this.isInitialized || !window.adsbygoogle) {
      return false;
    }

    try {
      // Check if Auto Ads are already enabled to avoid duplicate configuration
      if (window.adsbygoogleAutoAdsEnabled) {
        return true;
      }
      
      // Enable Auto Ads configuration (only once)
      (window.adsbygoogle = window.adsbygoogle || []).push({
        google_ad_client: "ca-pub-6690779336808264",
        enable_page_level_ads: true,
        overlays: {bottom: true},
        vignettes: {enabled: true}
      });
      
      // Mark as enabled to prevent duplicates
      window.adsbygoogleAutoAdsEnabled = true;
      
      return true;
    } catch (error) {
      // Silent error handling
      return false;
    }
  }

  // Setup global protection against duplicate Auto Ads
  setupGlobalProtection() {
    if (typeof window === 'undefined') return;
    
    // Override the global adsbygoogle push to prevent duplicate Auto Ads
    const originalPush = window.adsbygoogle?.push;
    if (originalPush) {
      window.adsbygoogle.push = (config) => {
        // If it's an Auto Ads configuration, check for duplicates
        if (config && typeof config === 'object' && config.enable_page_level_ads) {
          if (window.adsbygoogleAutoAdsEnabled) {
            // Already enabled, skip this push
            return;
          }
          window.adsbygoogleAutoAdsEnabled = true;
        }
        // Call original push for other configurations
        return originalPush.call(window.adsbygoogle, config);
      };
    }
  }

  // Get current status
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      currentPath: this.currentPath,
      adsbygoogleReady: !!window.adsbygoogle
    };
  }
}

// Create singleton instance
const autoAdsManager = new AutoAdsManager();

export default autoAdsManager;
